import { formatISO } from "date-fns";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    useApplyScheduleTemplateBulkMutation,
    useScheduleTemplates,
} from "@api/scheduleTemplate.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { InjectedCommandOption } from "@components/commands/lib";
import { ScheduleChangeConfirmationModal } from "../change/ScheduleChangeConfirmationModal";
import { buildBulkReplaceScheduleCopy } from "../change/scheduleChangeCopy";
import { requiresRecognitionStartDate } from "../scheduleApplyConfirmHelpers";
import {
    formatBulkScheduleAllFailedLabel,
    formatBulkScheduleBlockedSubject,
    getBulkScheduleConfirmLabel,
} from "./bulkScheduleCopy";
import {
    BulkScheduleNoun,
    BulkSchedulePartition,
    BulkScheduleRow,
    filterTemplatesForEntities,
    groupBulkScheduleItemsByEntity,
    partitionBulkScheduleRows,
    resolveBulkScheduleSpecificDate,
} from "./bulkScheduleRows";
import { BulkScheduleSpecificDateModal } from "./BulkScheduleSpecificDateModal";
import { SchedulesBulkApplyToast } from "./SchedulesBulkApplyToast";

interface UseBulkScheduleApplyParams {
    rows: BulkScheduleRow[];
    /** Selected rows left out before this hook, named in the confirm. */
    skippedWarning?: string;
    noun: BulkScheduleNoun;
    isEnabled: boolean;
    isFieldLocked: boolean;
    templateBlockReason?: (template: ScheduleTemplateListItem) => string | null;
    /** Policies the selection can never take are left out of the menu. */
    isTemplateEligible?: (template: ScheduleTemplateListItem) => boolean;
    toastKey: string;
    invalidate: () => Promise<void>;
    clearSelection: () => void;
}

interface PendingApply {
    template: ScheduleTemplateListItem;
    partition: BulkSchedulePartition;
    specificDate: string | null;
}

/** Which step of the apply is showing; the pending value outlives the close animation. */
type PendingStep = "date" | "replace" | null;

export function useBulkScheduleApply({
    rows,
    skippedWarning,
    noun,
    isEnabled,
    isFieldLocked,
    templateBlockReason,
    isTemplateEligible,
    toastKey,
    invalidate,
    clearSelection,
}: UseBulkScheduleApplyParams): {
    options: InjectedCommandOption[];
    isLoadingTemplates: boolean;
    hasTemplatesError: boolean;
    confirmModal: ReactNode;
} {
    const { toast, error } = useToaster();
    const applyBulk = useApplyScheduleTemplateBulkMutation();
    const {
        data: templates,
        isLoading: isLoadingTemplates,
        isError: hasTemplatesError,
    } = useScheduleTemplates({ enabled: isEnabled });
    const [pending, setPending] = useState<PendingApply | null>(null);
    const [step, setStep] = useState<PendingStep>(null);

    const entityIds = useMemo(
        () => [...new Set(rows.map((row) => row.entityId))],
        [rows],
    );
    const eligibleTemplates = useMemo(
        () =>
            filterTemplatesForEntities({
                templates: (templates ?? []).filter(
                    (template) => isTemplateEligible?.(template) ?? true,
                ),
                entityIds,
            }),
        [templates, entityIds, isTemplateEligible],
    );

    const run = useCallback(
        async (options: PendingApply) => {
            const { template, partition } = options;
            const specificDate = resolveBulkScheduleSpecificDate({
                template,
                specificDate: options.specificDate,
            });
            const failureReasons = [...partition.skippedReasons];
            let appliedCount = 0;
            try {
                for (const group of groupBulkScheduleItemsByEntity(
                    partition.items,
                )) {
                    for (
                        let index = 0;
                        index < group.sources.length;
                        index += APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT
                    ) {
                        const sources = group.sources.slice(
                            index,
                            index + APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT,
                        );
                        const result = await applyBulk.mutateAsync({
                            templateId: template.id,
                            payload: {
                                entityId: group.entityId,
                                specificDate,
                                sources,
                            },
                        });
                        appliedCount += result.applied.length;
                        failureReasons.push(
                            ...result.failed.map((item) => item.message),
                        );
                    }
                }
            } catch (applyError) {
                // Earlier requests committed; the rest of the batch was not sent.
                failureReasons.push(
                    applyError instanceof Error
                        ? applyError.message
                        : "Failed to apply schedule",
                );
            }
            if (appliedCount > 0) {
                clearSelection();
            }
            await invalidate();
            if (appliedCount === 0) {
                error(
                    formatBulkScheduleAllFailedLabel({
                        noun,
                        scheduleName: template.name,
                        count: partition.items.length,
                        reason: failureReasons[0],
                    }),
                );
                return;
            }
            toast(
                <SchedulesBulkApplyToast
                    scheduleName={template.name}
                    noun={noun}
                    appliedCount={appliedCount}
                    failedCount={
                        partition.skippedReasons.length +
                        (partition.items.length - appliedCount)
                    }
                    failureReason={failureReasons[0]}
                />,
                toastKey,
            );
        },
        [applyBulk, clearSelection, error, invalidate, noun, toast, toastKey],
    );

    const continueAfterDate = useCallback(
        (next: PendingApply) => {
            setPending(next);
            if (next.partition.replaceCount > 0) {
                setStep("replace");
                return;
            }
            setStep(null);
            void run(next);
        },
        [run],
    );

    const selectTemplate = useCallback(
        (template: ScheduleTemplateListItem) => {
            if (rows.length === 0) {
                return;
            }
            const partition = partitionBulkScheduleRows({
                rows,
                template,
            });
            if (partition.items.length === 0) {
                const subject = formatBulkScheduleBlockedSubject({
                    noun,
                    count: rows.length,
                });
                error(
                    partition.skippedReasons[0]
                        ? `${subject}: ${partition.skippedReasons[0]}`
                        : subject,
                );
                return;
            }
            if (requiresRecognitionStartDate(template)) {
                setPending({
                    template,
                    partition,
                    specificDate: formatISO(new Date(), {
                        representation: "date",
                    }),
                });
                setStep("date");
                return;
            }
            continueAfterDate({ template, partition, specificDate: null });
        },
        [continueAfterDate, error, noun, rows],
    );

    const options = useMemo(
        () =>
            eligibleTemplates.map((template): InjectedCommandOption => {
                const blockReason = templateBlockReason?.(template) ?? null;
                return {
                    id: `schedule-${template.id}`,
                    label: template.name,
                    sublabel: blockReason ?? undefined,
                    keywords: [template.name, template.group].filter(Boolean),
                    disabled:
                        isFieldLocked ||
                        applyBulk.isPending ||
                        blockReason != null,
                    onSelect: () => selectTemplate(template),
                };
            }),
        [
            applyBulk.isPending,
            eligibleTemplates,
            isFieldLocked,
            selectTemplate,
            templateBlockReason,
        ],
    );

    const confirmModal: ReactNode = (
        <>
            <BulkScheduleSpecificDateModal
                show={step === "date"}
                templateName={pending?.template.name ?? ""}
                noun={noun}
                count={pending?.partition.items.length ?? 0}
                specificDate={pending?.specificDate ?? null}
                onSpecificDateChange={(specificDate) =>
                    setPending((current) =>
                        current ? { ...current, specificDate } : current,
                    )
                }
                onConfirm={async () => {
                    if (!pending?.specificDate) {
                        return;
                    }
                    continueAfterDate(pending);
                }}
                onDismiss={() => setStep(null)}
            />
            <ScheduleChangeConfirmationModal
                show={step === "replace"}
                copy={
                    pending
                        ? buildBulkReplaceScheduleCopy({
                              noun,
                              replaceCount: pending.partition.replaceCount,
                              totalCount: pending.partition.items.length,
                              newScheduleName: pending.template.name,
                              skippedWarning,
                          })
                        : null
                }
                confirmLabel={getBulkScheduleConfirmLabel(noun)}
                onConfirm={async () => {
                    if (!pending) {
                        return;
                    }
                    setStep(null);
                    await run(pending);
                }}
                onDismiss={() => setStep(null)}
            />
        </>
    );

    return { options, isLoadingTemplates, hasTemplatesError, confirmModal };
}
